import cookie from 'cookie';
import { BASE_PATH, ROUTE_CODES } from 'JS/config/routes';

import config from '../../../../config/config';
import UserModel from '../UserModel';
import * as tokenHelper from '../../../helpers/TokenHelper';
import { getStore } from '../../../../store';
import { appUrl } from '../../../helpers/url';
import { makeRateLimitedFetch } from '../../requestLimiter';

import Projects from './projects';

const rateLimitedfetch = makeRateLimitedFetch(config.SERVICES_REQUEST_RATE_LIMIT, config.SERVICES_REQUEST_CONCURRENCY);

const PLATFORM = navigator.userAgent.includes('X-FunScribe') ? 'mobile' : 'web';
const VERSION = `FUNSCRIBE:${process.env.VSC_VERSION}:${PLATFORM}`;
const { SERVICES_URL, APPLICATION_NAME, THUMB_SIZE } = config;
const appJson = 'application/json';
const HTTP_SUCCESS_RANGE = {
  lower: 200,
  upper: 300
};

const getParsedCookies = () => cookie.parse(document.cookie);

const getRendererUserId = () => {
  return getParsedCookies()['rendererUser'];
};

export function rendererHeaders(withAuthToken) {
  const cookies = getParsedCookies();
  const headers = {};

  if (!cookies.rendererAuth || !cookies.rendererUser) {
    return headers;
  }

  headers['x-spk-renderer-auth'] = cookies.rendererAuth;
  headers['x-spk-renderer-user'] = cookies.rendererUser;
  headers['x-spk-app-name'] = APPLICATION_NAME;

  if (withAuthToken) {
    headers['x-spk-auth-token'] = cookies.rendererAuth;
  }

  return headers;
}

// Some helper functions for dealing with response data
function validateResponse(r, customErrorMessage) {
  return new Promise((resolve, reject) => {
    if (r.status >= HTTP_SUCCESS_RANGE.upper || r.status < HTTP_SUCCESS_RANGE.lower) {
      if (customErrorMessage) {
        const appServicesError = new Error(`${customErrorMessage} | Status: ${r.status}`);
        appServicesError.httpStatusCode = r.status;
        return reject(appServicesError);
      }

      r.text().then(errorText => {
        const appServicesError = new Error(errorText || r.statusText);
        appServicesError.httpStatusCode = r.status;
        reject(appServicesError);
      });
    } else {
      resolve(r);
    }
  });
}

function getObject(r) {
  return r.text().then(JSON.parse);
}

function getUID() {
  return tokenHelper.getUID();
}

class AppServices {
  constructor() {
    let hostname = SERVICES_URL;

    if (!hostname.endsWith('/')) {
      hostname += '/';
    }

    this._baseUrl = hostname;

    this.projects = new Projects(this);

    this._assetUrlCache = {};
  }

  getOptions(method = 'GET', body, contentType, skipAuth = false, user) {
    const headers = {
      Accept: appJson
    };

    // If we have content, default to appJson content-type
    if ((body || contentType) && contentType !== '' && contentType !== 'auto') {
      headers['Content-Type'] = contentType || appJson;
    }

    if (!skipAuth) {
      // If there is already an authtoken with the User then set the header
      if (user) {
        headers['x-spk-auth-token'] = user.token;
        headers['x-spk-app-name'] = APPLICATION_NAME;
      } else {
        // Otherwise check store state for the token and if there is one, set the header
        const { auth } = getStore().getState();
        const { currentUser } = auth;

        const { token } = currentUser;
        const authToken = token || tokenHelper.getSparkolToken();
        if (authToken) {
          headers['x-spk-auth-token'] = authToken;
          headers['x-spk-app-name'] = APPLICATION_NAME;
        }
      }
    } else {
      delete headers['x-spk-auth-token'];
    }

    return {
      method,
      headers: { ...headers, ...rendererHeaders() },
      body
    };
  }

  authenticate(email, password) {
    const url = `${this._baseUrl}session`;
    const options = this.getOptions(
      'POST',
      JSON.stringify({
        username: email,
        password,
        application: 'funscribe',
        environment: process.env.NODE_ENV,
        platform: PLATFORM,
        version: VERSION
      }),
      null,
      true
    );

    return rateLimitedfetch(url, options)
      .then(r => validateResponse(r, `Error creating a user session.`))
      .then(async response => {
        const { user, userTokens } = await response.json();
        const spkToken = response.headers.get('x-spk-auth-token');

        return new UserModel(user, spkToken, userTokens);
      });
  }

  renderCloud = (scribeId, visibility, segments, format, assets) => {
    const url = `${this._baseUrl}sho-co/render/cloud`;
    const body = {
      application: 'videoscribe-cloud',
      segments,
      assets,
      source: scribeId,
      applicationUrl: appUrl(),
      visibility,
      format
    };
    const options = this.getOptions('POST', JSON.stringify(body));
    return rateLimitedfetch(url, options)
      .then(r => validateResponse(r, `Error calling the renderer.`))
      .then(res => res.json());
  };

  fetchRenderProgress = shortId => {
    const url = `${this._baseUrl}sho-co/render/cloud/${shortId}/progress`;
    const options = this.getOptions('GET');
    return rateLimitedfetch(url, options)
      .then(r => validateResponse(r, `Error fetching render progress.`))
      .then(res => res.json());
  };

  getVideoDownloadUrl = (shortId, format) => {
    const url = `${this._baseUrl}sho-co/download/url/${shortId}/${format}`;
    const options = this.getOptions('GET');
    return rateLimitedfetch(url, options)
      .then(r => validateResponse(r, `Error fetching the render URL.`))
      .then(res => res.json());
  };

  getUser(uid = getUID() || getRendererUserId()) {
    const url = `${this._baseUrl}user/${uid}`;
    const options = this.getOptions('GET');

    return rateLimitedfetch(url, options)
      .then(r => validateResponse(r, `Error fetching user details for user id ${uid}`))
      .then(async response => {
        const user = await response.json();
        const spkToken = options.headers['x-spk-auth-token'];

        return new UserModel(user, spkToken);
      });
  }

  // Attempts to create a user via the App Services API endpoint
  createUser = (email, password, firstName, lastName, token, optInMarketingEmail) => {
    const url = `${this._baseUrl}register`;
    const options = this.getOptions(
      'POST',
      JSON.stringify({
        // App Services endpoint expects key of username (which is the user's email)
        username: email,
        password,
        // API endpoint expects the keys for firstname and lastname to be lowercase
        firstname: firstName,
        // App Services endpoint expects key of surname (which is the user's lastName)
        surname: lastName,
        optInMarketing: true,
        optInMarketingEmail,
        token,
        version: VERSION,
        platform: PLATFORM,
        environment: process.env.NODE_ENV,
        sourceProduct: 'funscribe',
        application: 'funscribe'
      })
    );

    return rateLimitedfetch(url, options)
      .then(validateResponse)
      .then(getObject)
      .catch(async res => {
        throw JSON.parse(res.message.toString());
      });
  };

  verify = token => {
    const url = `${this._baseUrl}verify`;
    const options = this.getOptions(
      'POST',
      JSON.stringify({
        token
      })
    );
    return rateLimitedfetch(url, options)
      .then(r => validateResponse(r, `Error verifying your user session.`))
      .catch(async res => {
        throw JSON.parse(res.message.toString());
      });
  };

  sendResetEmail = (email, token) => {
    const url = `${this._baseUrl}forgotten-password`;
    const options = this.getOptions(
      'POST',
      JSON.stringify({
        token,
        email,
        returnPathTemplate: `${BASE_PATH.replace(/\/$/, '')}${ROUTE_CODES.RESET_PASSWORD.replace(
          ':token',
          '{{token}}'
        )}`
      })
    );

    return rateLimitedfetch(url, options)
      .then(validateResponse)
      .catch(async res => {
        throw JSON.parse(res.message.toString());
      });
  };

  resetPassword = (password, token, resetToken, verificationCode) => {
    const url = `${this._baseUrl}reset-password`;
    const options = this.getOptions(
      'PUT',
      JSON.stringify({
        password,
        token,
        resetToken,
        confirmationCode: verificationCode
      })
    );

    return rateLimitedfetch(url, options)
      .then(validateResponse)
      .catch(async res => {
        throw JSON.parse(res.message.toString());
      });
  };

  // assets
  getAppData(key, user) {
    const uid = getUID();
    const url = `${this._baseUrl}user/${uid}/app-data/videoscribe-cloud/${key}`;
    const options = this.getOptions('GET', undefined, undefined, undefined, user);

    return rateLimitedfetch(url, options)
      .then(r => validateResponse(r, `Error fetching app data.`))
      .then(getObject);
  }

  setAppData(key, value) {
    const uid = getUID();
    const url = `${this._baseUrl}user/${uid}/app-data/videoscribe-cloud/${key}`;
    const options = this.getOptions('PUT', JSON.stringify(value));

    return rateLimitedfetch(url, options)
      .then(r => validateResponse(r, `Error setting app data.`))
      .then(r => r.json());
  }

  deleteAsset = assetId => {
    const url = `${this._baseUrl}project-asset/${assetId}`;
    const options = this.getOptions('DELETE');

    return rateLimitedfetch(url, options);
  };

  _getAssetUrl = assetId => {
    const url = `${this._baseUrl}project-asset/${assetId}`;
    const options = this.getOptions('GET');

    return rateLimitedfetch(url, options)
      .then(r => validateResponse(r, `Error fetching a project asset URL.`))
      .then(res => res.json())
      .then(res => ({ assetUrl: res.url, assetThumbs: res.thumbs }));
  };

  _getObjectUrl = url => {
    return window
      .fetch(url)
      .then(res => res.blob())
      .then(blob => URL.createObjectURL(blob));
  };

  _updateUrlCache = async assetId => {
    const { assetUrl, assetThumbs } = await this._getAssetUrl(assetId);
    let assetThumb;
    if (assetUrl) {
      assetThumb = assetThumbs.find(thumb => thumb.size === THUMB_SIZE);
      const assetObjectUrl = this._getObjectUrl(assetUrl);
      let assetThumbObjectUrl = assetThumb?.url ? await this._getObjectUrl(assetThumb.url) : undefined;
      this._assetUrlCache[assetId] = {
        assetUrl: assetObjectUrl,
        ...(assetThumbObjectUrl && { thumbUrl: assetThumbObjectUrl })
      };
    }
  };

  clearAssetCache = () => {
    Object.values(this._assetUrlCache).forEach(({ assetUrl, thumbUrl }) => {
      URL.revokeObjectURL(assetUrl);
      URL.revokeObjectURL(thumbUrl);
    });
    this._assetUrlCache = {};
  };

  getAssetUrl = async (assetId, thumb) => {
    if (!this._assetUrlCache.hasOwnProperty(assetId)) {
      await this._updateUrlCache(assetId);
    }
    if (thumb && this._assetUrlCache[assetId].thumbUrl) {
      return this._assetUrlCache[assetId].thumbUrl;
    }

    return this._assetUrlCache[assetId].assetUrl;
  };

  getAssetsList = () => {
    const url = `${this._baseUrl}project-asset`;
    const options = this.getOptions('GET');

    return rateLimitedfetch(url, options)
      .then(r => validateResponse(r, `Error fetching your project assets list.`))
      .then(getObject);
  };

  _uploadAsset = (assetBlob, filename) => {
    const url = `${this._baseUrl}project-asset`;

    const formData = new FormData();
    formData.append('assetFile', assetBlob, filename);

    const options = this.getOptions('POST', formData, 'auto');

    return rateLimitedfetch(url, options)
      .then(r => validateResponse(r, `Error uploading your project asset.`))
      .then(getObject);
  };

  uploadAsset = async (assetBlob, filename) => {
    const assetId = await this._uploadAsset(assetBlob, filename);

    // Populate asset cache on upload
    this._assetUrlCache[assetId] = {
      assetUrl: URL.createObjectURL(assetBlob)
    };

    return assetId;
  };

  getCursors = () => {
    const url = `${this._baseUrl}library/hands?results=1000`;
    const options = this.getOptions('GET');

    return rateLimitedfetch(url, options)
      .then(r => validateResponse(r, `Error fetching scribe cursors.`))
      .then(res => res.json())
      .then(res => res.items);
  };
}

export default AppServices;
export const appServices = new AppServices();
export { validateResponse, getObject };
